import React from 'react';
import { useTranslation } from 'react-i18next';
import SavingsDetails from './SavingsDetails';
import { formatCurrencyWhole } from '../utils/phevCalculations';
import { TooltipTranslation } from './SavingsDetails';
import { pushDataLayerEvent } from '../utils/analytics';

interface EstimatedSavingsProps {
  className?: string;
  currency: string;
  projectedSaving: number;
  projectionYears: number;
  yearlySaving: number;
  monthlySaving: number;
  weekdayKM: number;
  weekendKM: number;
  vehicle: {
    name: string;
    shortName?: string;
    phevElectricRange: number;
    phevHybridEconomy: number;
    phevElectricEfficiency: number;
  };
  translation?: TooltipTranslation;
  phevFuelCost: number;
  electricityCost: number;
  distanceUnit: string;
  iceYearlyCost: number;
  buildYoursUrl?: string;
}

const EstimatedSavings: React.FC<EstimatedSavingsProps> = ({
  className,
  currency,
  projectedSaving,
  projectionYears,
  yearlySaving,
  monthlySaving,
  weekdayKM,
  weekendKM,
  vehicle,
  translation,
  phevFuelCost,
  electricityCost,
  distanceUnit,
  iceYearlyCost,
  buildYoursUrl,
}) => {
  const { t, i18n } = useTranslation();

  const handleBuildYoursClick = () => {
    if (buildYoursUrl) {
      let host = "";

      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        host = "https://preprod.mazda.ca";
      } else {
        host = window.location.origin;
      }

      const url = host + buildYoursUrl;
      console.log(url);

      pushDataLayerEvent('calculator_conversion_click', {
        link_url: url,
        link_text: t('build_yours'),
        vehicle_name: vehicle.name,
      });
      // Use '_top' to break out of iframe and navigate the top-level tab
      // '_self' only navigates the iframe itself when hosted in an iframe
      try {
        if (window.top && window.top !== window.self) {
          window.top.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch {
        // Fallback for cross-origin iframe where top access is blocked
        window.open(url, '_top');
      }
    }
  };

  return (
    <aside className={`phev-calculator__sidebar ${className ?? ''}`.trim()}>
      <div className="phev-calculator__sidebar-content">
        <div className="phev-calculator__savings">
          <div className="phev-calculator__vehicle-name">
            <h5 className="phev-calculator__vehicle-title">{t('savingsCalculator')}</h5>
          </div>
          <div className="phev-calculator__savings-hero">
            <h1 className="phev-calculator__savings-amount">{formatCurrencyWhole(projectedSaving, currency, i18n.language)}</h1>
          </div>
          <span className="phev-calculator__savings-label">
            {t('projectionYearsSaving', { years: projectionYears })}
          </span>
          <div className="phev-calculator__savings-breakdown">
            <div className="phev-calculator__savings-breakdown-container">
              <h3 className="phev-calculator__savings-breakdown-value">
                <em className="phev-calculator__savings-breakdown-amount">{formatCurrencyWhole(yearlySaving, currency, i18n.language)}</em>
              </h3>
              {t('oneYear')}
            </div>
            <div className="phev-calculator__savings-breakdown-container">
              <h3 className="phev-calculator__savings-breakdown-value">
                <em className="phev-calculator__savings-breakdown-amount">{formatCurrencyWhole(monthlySaving, currency, i18n.language)}</em>
              </h3>
              {t('oneMonth')}
            </div>
          </div>
        </div>
        <div className="phev-calculator__collapse-container">
          <SavingsDetails
            weekdayKM={weekdayKM}
            weekendKM={weekendKM}
            vehicle={vehicle}
            translation={translation}
            phevFuelCost={phevFuelCost}
            electricityCost={electricityCost}
            currency={currency}
            year={t('year')}
            distanceUnit={distanceUnit}
            iceYearlyCost={iceYearlyCost}
            yearlySaving={yearlySaving}
          />
        </div>
        <div className="phev-calculator__build-yours">
          <button className="mz-btn mz-btn--primary mz-btn--red" style={{ width: '100%' }} onClick={handleBuildYoursClick}>
            <span>{t('build_yours')}</span>
          </button>
        </div>

      </div>
    </aside>
  );
};

export default EstimatedSavings;
